"use client"

import { EarthIcon } from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { motion } from "framer-motion"
import { useCallback, useEffect, useRef, useState } from "react"
import { cn, formatRelativeTime } from "@/lib/utils"
import type { Ga4Response } from "@/types/ga4"

export function VisitorCities({ className }: { className?: string }) {
    const [data, setData] = useState<Ga4Response | null>(null)
    const [loading, setLoading] = useState(true)
    const [showLeftShadow, setShowLeftShadow] = useState(false)
    const [showRightShadow, setShowRightShadow] = useState(false)
    const scrollRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        async function fetchData() {
            try {
                const response = await fetch("/api/analytics/cities", {
                    next: { revalidate: 3600 },
                })
                const result = await response.json()
                setData(result)
            } catch (error) {
                console.error("Failed to fetch cities:", error)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [])

    const handleScroll = useCallback(() => {
        const el = scrollRef.current
        if (!el) return
        setShowLeftShadow(el.scrollLeft > 0)
        setShowRightShadow(el.scrollLeft + el.clientWidth < el.scrollWidth)
    }, [])

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        requestAnimationFrame(() => handleScroll())
        el.addEventListener("scroll", handleScroll)
        return () => el.removeEventListener("scroll", handleScroll)
    }, [handleScroll])

    if (loading || !data?.cities.length) return null

    return (
        <div
            className={cn(
                "border-border/70 border-t pt-6 dark:border-border/40",
                className
            )}
        >
            <div className="mb-4 flex flex-col items-center space-y-1 text-center md:flex-row md:justify-between md:text-left">
                <h3 className="flex items-center gap-2 font-semibold text-gray-600 text-sm dark:text-gray-400">
                    <HugeiconsIcon icon={EarthIcon} className="size-5" />
                    Recent Visitors From
                </h3>
                <p className="text-center font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
                    Updated {formatRelativeTime(new Date(data.lastUpdated))}
                </p>
            </div>
            <div className="relative w-full min-w-0">
                <div
                    ref={scrollRef}
                    className="scrollbar-hide flex w-full gap-2 overflow-x-auto whitespace-nowrap"
                    onScroll={handleScroll}
                >
                    {data.cities.map(
                        (
                            city: { city: string; visitorCount: number },
                            index: number
                        ) => (
                            <motion.span
                                key={city.city}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border/70 px-3 py-1 font-medium text-gray-700 text-xs dark:border-border/40 dark:text-gray-300"
                            >
                                {city.city}
                                <span className="text-gray-400 dark:text-gray-500">
                                    ({city.visitorCount})
                                </span>
                            </motion.span>
                        )
                    )}
                </div>
                <div
                    className={`pointer-events-none absolute inset-y-0 left-0 w-8 bg-linear-to-r from-background to-transparent transition-opacity ${showLeftShadow ? "opacity-100" : "opacity-0"}`}
                />
                <div
                    className={`pointer-events-none absolute inset-y-0 right-0 w-8 bg-linear-to-l from-background to-transparent transition-opacity ${showRightShadow ? "opacity-100" : "opacity-0"}`}
                />
            </div>
        </div>
    )
}
